import React, { useEffect, useState } from 'react';
import { VendorDashboard } from './VendorDashboard';
import { VendorList } from './VendorList';
import { VendorOnboarding } from './VendorOnboarding';
import { VendorDetails } from './VendorDetails';
import { ComplianceCheck } from './ComplianceCheck';
export function VendorManagement({
  locale = 'en-US',
  currentView: parentView
}) {
  const [currentView, setCurrentView] = useState(parentView || 'dashboard');
  const [selectedVendor, setSelectedVendor] = useState(null);
  // Sync internal view state with parent view prop when it changes
  useEffect(() => {
    if (parentView && parentView !== 'vendor-management') {
      // Extract the view name after the vendor- prefix
      const viewName = parentView.replace('vendor-', '');
      setCurrentView(viewName);
    }
  }, [parentView]);
  // Sample entities and sites data - in a real app this would come from an API
  const entities = [{
    id: 'ent-001',
    name: 'Global Technologies Inc.',
    sites: [{
      id: 'site-001',
      name: 'New York HQ'
    }, {
      id: 'site-002',
      name: 'San Francisco Office'
    }]
  }, {
    id: 'ent-002',
    name: 'European Operations Ltd',
    sites: [{
      id: 'site-003',
      name: 'London Office'
    }, {
      id: 'site-004',
      name: 'Paris Branch'
    }]
  }];
  // Generate 1000 sample vendors
  const generateVendors = () => {
    const vendorTypes = ['Supplier', 'IT Equipment', 'Printing Services', 'Consulting', 'Logistics', 'Marketing', 'Legal Services', 'Facilities', 'Manufacturing', 'Software', 'Healthcare', 'Construction', 'Telecommunications', 'Food Services', 'Office Supplies', 'Utilities', 'Financial Services', 'Security', 'Cleaning Services', 'Training'];
    const countries = ['United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Japan', 'China', 'Australia', 'Brazil', 'India', 'Mexico', 'South Africa', 'Singapore', 'Netherlands', 'Sweden', 'Switzerland', 'Ireland', 'United Arab Emirates'];
    const complianceStatuses = ['Compliant', 'Pending Review', 'Incomplete', 'Non-Compliant', 'Not Started'];
    const statuses = ['Active', 'Pending Onboarding', 'Inactive', 'Invited'];
    const accountTypes = ['Checking', 'Savings', 'Current', 'Business', 'Corporate', 'Money Market', 'Investment', 'Trust'];
    // Generate initial sample vendors (the ones we already had, but with enhanced compliance data)
    const initialVendors = [{
      id: 'VEN-2023-001',
      name: 'Office Depot',
      status: 'Active',
      complianceStatus: 'Compliant',
      type: 'Supplier',
      country: 'United States',
      onboardingDate: '2023-01-15',
      lastUpdated: '2023-09-20',
      isGlobal: true,
      entities: [],
      sites: [],
      contacts: [{
        id: 1,
        name: 'John Smith',
        email: 'john.smith@officedepot.com',
        phone: '+1 (555) 123-4567',
        role: 'Account Manager'
      }],
      documents: [{
        id: 1,
        name: 'Insurance Certificate',
        type: 'Insurance',
        uploadDate: '2023-01-15',
        expiryDate: '2024-01-15',
        status: 'Valid'
      }, {
        id: 2,
        name: 'Tax Registration',
        type: 'Tax',
        uploadDate: '2023-01-15',
        expiryDate: null,
        status: 'Valid'
      }, {
        id: 3,
        name: 'Business License',
        type: 'Legal',
        uploadDate: '2023-01-15',
        expiryDate: '2024-02-28',
        status: 'Valid'
      }, {
        id: 4,
        name: 'W-9 Form',
        type: 'Tax',
        uploadDate: '2023-01-15',
        expiryDate: null,
        status: 'Valid'
      }],
      // Add ESG data
      esg: {
        score: 87,
        environmental: 85,
        social: 90,
        governance: 86,
        lastUpdated: '2023-08-15',
        summary: 'Strong sustainability program with carbon-neutral operations and ethical sourcing policies.'
      },
      // Add bank account
      bankAccounts: [{
        id: 1,
        bankName: 'Chase Bank',
        accountName: 'Office Depot Inc.',
        accountNumber: '123456789012',
        routingNumber: '021000021',
        swiftCode: 'CHASUS33',
        iban: '',
        accountType: 'Business',
        isPrimary: true,
        country: 'United States'
      }],
      // Add compliance details
      complianceDetails: {
        score: 98,
        lastChecked: '2023-09-15',
        issues: [],
        notes: 'All compliance requirements met. Annual review scheduled for January 2024.',
        requiredDocuments: [{
          name: 'Insurance Certificate',
          status: 'Provided'
        }, {
          name: 'Tax Registration',
          status: 'Provided'
        }, {
          name: 'Business License',
          status: 'Provided'
        }, {
          name: 'W-9 Form',
          status: 'Provided'
        }]
      }
    }, {
      id: 'VEN-2023-002',
      name: 'Dell Technologies',
      status: 'Active',
      complianceStatus: 'Pending Review',
      type: 'IT Equipment',
      country: 'United States',
      onboardingDate: '2023-02-10',
      lastUpdated: '2023-10-05',
      isGlobal: false,
      entities: ['ent-001'],
      sites: [],
      contacts: [{
        id: 1,
        name: 'Sarah Johnson',
        email: 'sarah.johnson@dell.com',
        phone: '+1 (555) 987-6543',
        role: 'Sales Representative'
      }],
      documents: [{
        id: 1,
        name: 'Insurance Certificate',
        type: 'Insurance',
        uploadDate: '2023-02-10',
        expiryDate: '2023-10-10',
        status: 'Expired'
      }, {
        id: 2,
        name: 'Tax Registration',
        type: 'Tax',
        uploadDate: '2023-02-10',
        expiryDate: null,
        status: 'Valid'
      }],
      // Add ESG data
      esg: {
        score: 72,
        environmental: 68,
        social: 75,
        governance: 73,
        lastUpdated: '2023-07-20',
        summary: 'Good progress on reducing environmental impact with room for improvement in supply chain transparency.'
      },
      // Add bank account
      bankAccounts: [{
        id: 1,
        bankName: 'Bank of America',
        accountName: 'Dell Technologies Inc.',
        accountNumber: '987654321098',
        routingNumber: '026009593',
        swiftCode: 'BOFAUS3N',
        iban: '',
        accountType: 'Corporate',
        isPrimary: true,
        country: 'United States'
      }],
      // Add compliance details
      complianceDetails: {
        score: 65,
        lastChecked: '2023-10-01',
        issues: [{
          severity: 'High',
          description: 'Insurance certificate expired on 2023-10-10'
        }, {
          severity: 'Medium',
          description: 'Missing modern slavery statement'
        }],
        notes: 'Awaiting updated insurance certificate. Vendor has been notified.',
        requiredDocuments: [{
          name: 'Insurance Certificate',
          status: 'Expired'
        }, {
          name: 'Tax Registration',
          status: 'Provided'
        }, {
          name: 'Modern Slavery Statement',
          status: 'Missing'
        }, {
          name: 'Business License',
          status: 'Provided'
        }]
      }
    }, {
      id: 'VEN-2023-003',
      name: 'PrintWorks Inc.',
      status: 'Pending Onboarding',
      complianceStatus: 'Incomplete',
      type: 'Printing Services',
      country: 'Canada',
      onboardingDate: '2023-09-30',
      lastUpdated: '2023-09-30',
      isGlobal: false,
      entities: ['ent-001'],
      sites: ['site-001'],
      contacts: [{
        id: 1,
        name: 'Michael Brown',
        email: 'm.brown@printworks.ca',
        phone: '+1 (555) 234-5678',
        role: 'Owner'
      }],
      documents: [],
      // Add ESG data
      esg: {
        score: 45,
        environmental: 40,
        social: 60,
        governance: 35,
        lastUpdated: '2023-09-30',
        summary: 'Moderate performance with significant areas for improvement, especially in environmental practices.'
      },
      // No bank account yet
      bankAccounts: [],
      // Add compliance details
      complianceDetails: {
        score: 30,
        lastChecked: '2023-09-30',
        issues: [{
          severity: 'Critical',
          description: 'No insurance documentation provided'
        }, {
          severity: 'High',
          description: 'Missing tax registration information'
        }, {
          severity: 'High',
          description: 'No bank account information'
        }],
        notes: 'Onboarding incomplete. Multiple required documents missing.',
        requiredDocuments: [{
          name: 'Insurance Certificate',
          status: 'Missing'
        }, {
          name: 'Tax Registration',
          status: 'Missing'
        }, {
          name: 'Business License',
          status: 'Missing'
        }]
      }
    },
    // Adding new vendor examples with clear compliance pass/fail cases
    {
      id: 'VEN-2023-004',
      name: 'Global Logistics Partners',
      status: 'Active',
      complianceStatus: 'Non-Compliant',
      type: 'Logistics',
      country: 'United Kingdom',
      onboardingDate: '2023-03-15',
      lastUpdated: '2023-10-12',
      isGlobal: true,
      entities: [],
      sites: [],
      contacts: [{
        id: 1,
        name: 'James Wilson',
        email: 'j.wilson@glpartners.co.uk',
        phone: '+44 20 1234 5678',
        role: 'Managing Director'
      }],
      documents: [{
        id: 1,
        name: 'Insurance Certificate',
        type: 'Insurance',
        uploadDate: '2023-03-15',
        expiryDate: '2023-09-15',
        status: 'Expired'
      }, {
        id: 2,
        name: 'VAT Registration',
        type: 'Tax',
        uploadDate: '2023-03-15',
        expiryDate: null,
        status: 'Valid'
      }],
      esg: {
        score: 58,
        environmental: 45,
        social: 65,
        governance: 64,
        lastUpdated: '2023-06-10',
        summary: 'Below average environmental practices with concerns about carbon footprint in transportation fleet.'
      },
      bankAccounts: [{
        id: 1,
        bankName: 'Barclays',
        accountName: 'Global Logistics Partners Ltd',
        accountNumber: '12345678',
        routingNumber: '20-00-00',
        swiftCode: 'BARCGB22',
        iban: 'GB29NWBK60161331926819',
        accountType: 'Business',
        isPrimary: true,
        country: 'United Kingdom'
      }],
      complianceDetails: {
        score: 35,
        lastChecked: '2023-10-12',
        issues: [{
          severity: 'Critical',
          description: 'Failed background check - legal proceedings in progress'
        }, {
          severity: 'High',
          description: 'Insurance certificate expired on 2023-09-15'
        }, {
          severity: 'Medium',
          description: 'Anti-bribery policy outdated'
        }],
        notes: 'Vendor has been flagged for compliance review due to ongoing legal issues and expired insurance.',
        requiredDocuments: [{
          name: 'Insurance Certificate',
          status: 'Expired'
        }, {
          name: 'VAT Registration',
          status: 'Provided'
        }, {
          name: 'Anti-bribery Policy',
          status: 'Outdated'
        }, {
          name: 'Background Check',
          status: 'Failed'
        }]
      }
    }, {
      id: 'VEN-2023-005',
      name: 'TechSolutions Corp',
      status: 'Active',
      complianceStatus: 'Compliant',
      type: 'Software',
      country: 'United States',
      onboardingDate: '2023-05-20',
      lastUpdated: '2023-10-15',
      isGlobal: true,
      entities: [],
      sites: [],
      contacts: [{
        id: 1,
        name: 'Lisa Chen',
        email: 'l.chen@techsolutions.com',
        phone: '+1 (555) 765-4321',
        role: 'Account Executive'
      }],
      documents: [{
        id: 1,
        name: 'Insurance Certificate',
        type: 'Insurance',
        uploadDate: '2023-05-20',
        expiryDate: '2024-05-20',
        status: 'Valid'
      }, {
        id: 2,
        name: 'Tax Registration',
        type: 'Tax',
        uploadDate: '2023-05-20',
        expiryDate: null,
        status: 'Valid'
      }, {
        id: 3,
        name: 'ISO 27001 Certification',
        type: 'Compliance',
        uploadDate: '2023-05-20',
        expiryDate: '2025-05-20',
        status: 'Valid'
      }, {
        id: 4,
        name: 'SOC 2 Report',
        type: 'Compliance',
        uploadDate: '2023-05-20',
        expiryDate: '2024-05-20',
        status: 'Valid'
      }],
      esg: {
        score: 92,
        environmental: 95,
        social: 88,
        governance: 93,
        lastUpdated: '2023-09-05',
        summary: 'Industry leader in sustainability with carbon-negative operations and strong diversity initiatives.'
      },
      bankAccounts: [{
        id: 1,
        bankName: 'Silicon Valley Bank',
        accountName: 'TechSolutions Corporation',
        accountNumber: '5678901234',
        routingNumber: '121140399',
        swiftCode: 'SVBKUS6S',
        iban: '',
        accountType: 'Corporate',
        isPrimary: true,
        country: 'United States'
      }],
      complianceDetails: {
        score: 97,
        lastChecked: '2023-10-15',
        issues: [],
        notes: 'Exemplary compliance record. All requirements exceeded with strong documentation.',
        requiredDocuments: [{
          name: 'Insurance Certificate',
          status: 'Provided'
        }, {
          name: 'Tax Registration',
          status: 'Provided'
        }, {
          name: 'ISO 27001 Certification',
          status: 'Provided'
        }, {
          name: 'SOC 2 Report',
          status: 'Provided'
        }, {
          name: 'Modern Slavery Statement',
          status: 'Provided'
        }, {
          name: 'Code of Conduct',
          status: 'Provided'
        }]
      }
    }];
    // ESG score ranges for random generation
    const esgScoreRanges = [{
      min: 80,
      max: 100
    }, {
      min: 60,
      max: 79
    }, {
      min: 40,
      max: 59
    }, {
      min: 0,
      max: 39
    } // Needs Review
    ];
    // Generate additional 995 vendors (to account for the 5 detailed ones we already have)
    const additionalVendors = Array.from({
      length: 995
    }, (_, i) => {
      const vendorId = `VEN-2023-${String(i + 6).padStart(3, '0')}`;
      const vendorName = `Vendor ${i + 6}`;
      const vendorType = vendorTypes[Math.floor(Math.random() * vendorTypes.length)];
      const country = countries[Math.floor(Math.random() * countries.length)];
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const complianceStatus = complianceStatuses[Math.floor(Math.random() * complianceStatuses.length)];
      const isGlobal = Math.random() > 0.7;
      // Generate random dates within the last 3 years
      const randomDate = () => {
        const start = new Date(2020, 0, 1);
        const end = new Date();
        const randomDate = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
        return randomDate.toISOString().split('T')[0];
      };
      const onboardingDate = randomDate();
      const lastUpdated = randomDate();
      // Randomly assign entities and sites
      const vendorEntities = [];
      if (!isGlobal && Math.random() > 0.3) {
        if (Math.random() > 0.5) vendorEntities.push('ent-001');
        if (Math.random() > 0.5) vendorEntities.push('ent-002');
      }
      const vendorSites = [];
      if (vendorEntities.includes('ent-001')) {
        if (Math.random() > 0.5) vendorSites.push('site-001');
        if (Math.random() > 0.5) vendorSites.push('site-002');
      }
      if (vendorEntities.includes('ent-002')) {
        if (Math.random() > 0.5) vendorSites.push('site-003');
        if (Math.random() > 0.5) vendorSites.push('site-004');
      }
      // Randomly decide if vendor has ESG data (70% chance)
      const hasESG = Math.random() > 0.3;
      // Generate ESG data if applicable
      let esg = null;
      if (hasESG) {
        // Randomly select a score range
        const scoreRange = esgScoreRanges[Math.floor(Math.random() * esgScoreRanges.length)];
        // Generate random score within that range
        const score = Math.floor(Math.random() * (scoreRange.max - scoreRange.min + 1)) + scoreRange.min;
        // Generate component scores with some variation around the main score
        const variation = 10;
        const environmental = Math.max(0, Math.min(100, score + (Math.random() * variation * 2 - variation)));
        const social = Math.max(0, Math.min(100, score + (Math.random() * variation * 2 - variation)));
        const governance = Math.max(0, Math.min(100, score + (Math.random() * variation * 2 - variation)));
        esg = {
          score: Math.round(score),
          environmental: Math.round(environmental),
          social: Math.round(social),
          governance: Math.round(governance),
          lastUpdated: randomDate()
        };
      }
      // Generate bank account data (60% chance)
      const hasBankAccount = Math.random() > 0.4;
      const bankAccounts = hasBankAccount ? [{
        id: 1,
        bankName: `Bank of ${country}`,
        accountName: `${vendorName} Ltd`,
        accountNumber: Math.floor(Math.random() * 10000000000).toString(),
        routingNumber: Math.floor(Math.random() * 1000000000).toString(),
        swiftCode: 'SWIFT' + Math.floor(Math.random() * 10000).toString(),
        iban: '',
        accountType: accountTypes[Math.floor(Math.random() * accountTypes.length)],
        isPrimary: true,
        country: country
      }] : [];
      // Generate compliance details based on complianceStatus
      let complianceDetails = null;
      if (complianceStatus === 'Compliant') {
        complianceDetails = {
          score: Math.floor(Math.random() * 20) + 80,
          lastChecked: randomDate(),
          issues: [],
          notes: 'All compliance requirements met.',
          requiredDocuments: [{
            name: 'Insurance Certificate',
            status: 'Provided'
          }, {
            name: 'Tax Registration',
            status: 'Provided'
          }, {
            name: 'Business License',
            status: 'Provided'
          }]
        };
      } else if (complianceStatus === 'Pending Review') {
        complianceDetails = {
          score: Math.floor(Math.random() * 20) + 60,
          lastChecked: randomDate(),
          issues: [{
            severity: 'Low',
            description: 'Documentation under review'
          }],
          notes: 'Vendor documentation is currently under review.',
          requiredDocuments: [{
            name: 'Insurance Certificate',
            status: 'Under Review'
          }, {
            name: 'Tax Registration',
            status: 'Provided'
          }, {
            name: 'Business License',
            status: 'Provided'
          }]
        };
      } else if (complianceStatus === 'Incomplete') {
        complianceDetails = {
          score: Math.floor(Math.random() * 20) + 40,
          lastChecked: randomDate(),
          issues: [{
            severity: 'Medium',
            description: 'Missing required documentation'
          }],
          notes: 'Vendor needs to provide additional documentation.',
          requiredDocuments: [{
            name: 'Insurance Certificate',
            status: 'Missing'
          }, {
            name: 'Tax Registration',
            status: 'Provided'
          }, {
            name: 'Business License',
            status: 'Provided'
          }]
        };
      } else if (complianceStatus === 'Non-Compliant') {
        complianceDetails = {
          score: Math.floor(Math.random() * 30) + 10,
          lastChecked: randomDate(),
          issues: [{
            severity: 'Critical',
            description: 'Failed compliance check'
          }, {
            severity: 'High',
            description: 'Multiple documents missing or expired'
          }],
          notes: 'Vendor has significant compliance issues that need to be addressed.',
          requiredDocuments: [{
            name: 'Insurance Certificate',
            status: 'Expired'
          }, {
            name: 'Tax Registration',
            status: 'Missing'
          }, {
            name: 'Business License',
            status: 'Invalid'
          }]
        };
      } else {
        // Not Started
        complianceDetails = {
          score: 0,
          lastChecked: null,
          issues: [{
            severity: 'High',
            description: 'Compliance check not initiated'
          }],
          notes: 'Compliance verification process has not been started.',
          requiredDocuments: [{
            name: 'Insurance Certificate',
            status: 'Not Started'
          }, {
            name: 'Tax Registration',
            status: 'Not Started'
          }, {
            name: 'Business License',
            status: 'Not Started'
          }]
        };
      }
      return {
        id: vendorId,
        name: vendorName,
        status,
        complianceStatus,
        type: vendorType,
        country,
        onboardingDate,
        lastUpdated,
        isGlobal,
        entities: vendorEntities,
        sites: vendorSites,
        contacts: [{
          id: 1,
          name: `Contact ${i + 6}`,
          email: `contact${i + 6}@example.com`,
          phone: `+1 (555) ${String(100 + i).padStart(3, '0')}-${String(1000 + i).slice(1)}`,
          role: 'Primary Contact'
        }],
        documents: Math.random() > 0.5 ? [{
          id: 1,
          name: 'Insurance Certificate',
          type: 'Insurance',
          uploadDate: onboardingDate,
          expiryDate: Math.random() > 0.8 ? '2023-10-10' : '2024-10-10',
          status: Math.random() > 0.8 ? 'Expired' : 'Valid'
        }] : [],
        esg,
        bankAccounts,
        complianceDetails
      };
    });
    return [...initialVendors, ...additionalVendors];
  };
  // Sample vendors data - in a real app this would come from an API
  const [vendors, setVendors] = useState(generateVendors());
  const handleAddVendor = vendorData => {
    const newVendor = {
      id: `VEN-2023-${String(vendors.length + 1).padStart(3, '0')}`,
      ...vendorData,
      onboardingDate: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    setVendors([...vendors, newVendor]);
    setCurrentView('vendors');
  };
  const handleUpdateVendor = updatedVendor => {
    setVendors(vendors.map(vendor => vendor.id === updatedVendor.id ? updatedVendor : vendor));
    setSelectedVendor(null);
    setCurrentView('vendors');
  };
  const handleSendOnboardingInvite = vendorData => {
    // In a real app, this would send an email with a secure link
    console.log('Sending onboarding invite to:', vendorData.email);
    // Add to vendors list as pending
    const newVendor = {
      id: `VEN-2023-${String(vendors.length + 1).padStart(3, '0')}`,
      name: vendorData.name,
      status: 'Invited',
      complianceStatus: 'Not Started',
      type: vendorData.type || '',
      country: vendorData.country || '',
      onboardingDate: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0],
      isGlobal: vendorData.isGlobal || true,
      entities: vendorData.entities || [],
      sites: vendorData.sites || [],
      contacts: [{
        id: 1,
        name: vendorData.contactName || '',
        email: vendorData.email,
        phone: vendorData.phone || '',
        role: vendorData.role || 'Primary Contact'
      }],
      documents: []
    };
    setVendors([...vendors, newVendor]);
    setCurrentView('vendors');
  };
  const handleViewVendor = vendor => {
    if (!vendor) return;
    setSelectedVendor(vendor);
    setCurrentView('vendor-details');
  };
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
      case 'vendor-management':
        return <VendorDashboard vendors={vendors} setCurrentView={setCurrentView} locale={locale} data-id="element-19768" />;
      case 'vendors':
        return <VendorList vendors={vendors} onViewVendor={handleViewVendor} setCurrentView={setCurrentView} locale={locale} data-id="element-19769" />;
      case 'onboard-vendor':
        return <VendorOnboarding onSave={handleAddVendor} onSendInvite={handleSendOnboardingInvite} setCurrentView={setCurrentView} entities={entities} locale={locale} data-id="element-19770" />;
      case 'vendor-details':
        return selectedVendor ? <VendorDetails vendor={selectedVendor} onUpdate={handleUpdateVendor} setCurrentView={setCurrentView} entities={entities} locale={locale} data-id="element-19771" /> : <div className="text-center py-8" data-id="element-19772">
            <p className="text-gray-500" data-id="element-19773">
              No vendor selected. Please select a vendor from the list.
            </p>
            <button onClick={() => setCurrentView('vendors')} className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19774">
              View Vendors
            </button>
          </div>;
      case 'compliance-check':
        return <ComplianceCheck vendor={selectedVendor} onUpdate={handleUpdateVendor} setCurrentView={setCurrentView} locale={locale} data-id="element-19775" />;
      default:
        return <VendorDashboard vendors={vendors} setCurrentView={setCurrentView} locale={locale} data-id="element-19776" />;
    }
  };
  return <div data-id="element-19777">
      <div className="mb-8" data-id="element-19778">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-19779">Vendor Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-19780">
          Manage your vendor relationships from onboarding to compliance
        </p>
      </div>
      {renderContent()}
    </div>;
}